import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../data/api'

const PAGE_SIZE = 20

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Server-side paginated + searchable + date-filterable — a popular voucher
// could have thousands of redemptions, so unlike a quick modal this needs
// to behave like the rest of the superadmin's list views (users/exams/etc).
export default function VoucherRedemptionsPage() {
  const { voucherId } = useParams()
  const navigate = useNavigate()

  const [data,   setData]   = useState(null) // { voucher, redemptions, total, totalPages }
  const [search, setSearch] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate,   setToDate]   = useState('')
  const [page, setPage] = useState(1)

  function fetchRedemptions(pg = page, q = search, from = fromDate, to = toDate) {
    setData(null)
    const params = new URLSearchParams({
      page: pg, limit: PAGE_SIZE,
      ...(q    ? { search: q } : {}),
      ...(from ? { from } : {}),
      ...(to   ? { to } : {}),
    })
    api.get(`/api/vouchers/${voucherId}/redemptions?${params}`)
      .then(setData)
      .catch(() => setData({ voucher: null, redemptions: [], total: 0, totalPages: 1 }))
  }

  useEffect(() => { fetchRedemptions(1, search, fromDate, toDate) }, [voucherId]) // eslint-disable-line react-hooks/exhaustive-deps

  function applyFilters({ q = search, from = fromDate, to = toDate } = {}) {
    setSearch(q); setFromDate(from); setToDate(to); setPage(1)
    fetchRedemptions(1, q, from, to)
  }

  function goToPage(p) {
    setPage(p)
    fetchRedemptions(p, search, fromDate, toDate)
  }

  const voucher = data?.voucher

  return (
    <div className="px-4 sm:px-40 py-6 sm:py-12 animate-fade-up">
      <button onClick={() => navigate('/superadmin', { state: { tab: 'settings' } })} className="btn-ghost mb-6 text-sm">← Back</button>

      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>Vouchers</p>
        <h2 className="text-2xl font-black tracking-tight mb-1" style={{ color: 'var(--text-1)' }}>Redemptions</h2>
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>
          {voucher ? `${voucher.name} (${voucher.code}) — ₹${voucher.price} credits at ${voucher.discountPct}% off` : 'Loading…'}
          {data && ` · ${data.total} redemption${data.total === 1 ? '' : 's'}`}
        </p>
      </div>

      <div className="card p-4 mb-4 flex flex-wrap items-center gap-2">
        <input type="text" value={search} onChange={e => applyFilters({ q: e.target.value })}
          placeholder="Search"
          className="text-xs rounded-lg px-3 py-1.5 outline-none w-full sm:w-40"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>From</span>
        <input type="date" value={fromDate} max={toDate || undefined}
          onChange={e => applyFilters({ from: e.target.value })}
          className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>To</span>
        <input type="date" value={toDate} min={fromDate || undefined}
          onChange={e => applyFilters({ to: e.target.value })}
          className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
        {(search || fromDate || toDate) && (
          <button onClick={() => applyFilters({ q: '', from: '', to: '' })}
            className="text-xs font-medium px-2 py-1 rounded-lg" style={{ color: 'var(--text-3)' }}>
            ✕ Clear
          </button>
        )}
      </div>

      {!data ? (
        <p className="text-sm py-6 text-center" style={{ color: 'var(--text-3)' }}>Loading…</p>
      ) : data.redemptions.length === 0 ? (
        <p className="text-sm py-6 text-center" style={{ color: 'var(--text-3)' }}>No redemptions match this search/date range.</p>
      ) : (
        <div className="space-y-1.5">
          {data.redemptions.map((r, i) => (
            <div key={i} className="card flex items-center justify-between gap-2 flex-wrap px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{r.accountName}</p>
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>{r.accountEmail}</p>
              </div>
              <p className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>{fmtDate(r.paidAt)}</p>
            </div>
          ))}

          {data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4 flex-wrap">
              <button onClick={() => goToPage(Math.max(1, page - 1))} disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>← Prev</button>
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>Page {page} of {data.totalPages}</span>
              <button onClick={() => goToPage(Math.min(data.totalPages, page + 1))} disabled={page === data.totalPages}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>Next →</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
